import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MarketButton, MarketPill } from '@market/react';
import CloseIcon from '@square-icons/react/16/UI/x';
import {
  formatProcessingRateParts,
  formatTierMonthlyPrice,
} from './bundle_offer_utils';

export const COMPARE_PLANS_MODAL_TEST_ID = 'compare-plans-modal';

const PLAN_ORDER = Object.freeze(['free', 'plus', 'premium']);
const RECOMMENDED_PLAN_COLOR = 'var(--core-blue-fill-color)';
const COMPARE_PLANS_BORDER_RADIUS = 4;
const RECOMMENDED_PLAN_BUTTON_STYLES = Object.freeze({
  '--button-focus-ring-color': RECOMMENDED_PLAN_COLOR,
  '--button-normal-variant-primary-rank-hover-state-background-color':
    'var(--core-blue-20-color)',
  '--button-normal-variant-primary-rank-normal-state-background-color':
    RECOMMENDED_PLAN_COLOR,
  '--button-normal-variant-primary-rank-pressed-state-background-color':
    'var(--core-blue-10-color)',
  '--button-normal-variant-primary-rank-hover-state-label-color':
    'var(--core-constant-white-color)',
  '--button-normal-variant-primary-rank-normal-state-label-color':
    'var(--core-constant-white-color)',
  '--button-normal-variant-primary-rank-pressed-state-label-color':
    'var(--core-constant-white-color)',
});

const PROCESSING_RATE_LABEL_KEYS = Object.freeze({
  card_present: 'cart.bundleOffer.comparePlans.processingRates.cardPresent',
  online: 'cart.bundleOffer.comparePlans.processingRates.online',
});

const PLAN_CONFIG = Object.freeze({
  free: {
    taglineKey: 'cart.bundleOffer.comparePlans.free.tagline',
    featureKeys: [
      'cart.bundleOffer.comparePlans.free.features.posAppAnyPayment',
      'cart.bundleOffer.comparePlans.free.features.onlineSite',
      'cart.bundleOffer.comparePlans.free.features.itemLibrary',
      'cart.bundleOffer.comparePlans.free.features.invoicing',
      'cart.bundleOffer.comparePlans.free.features.booking',
      'cart.bundleOffer.comparePlans.free.features.checkingSavings',
    ],
  },
  plus: {
    taglineKey: 'cart.bundleOffer.comparePlans.plus.tagline',
    featureKeys: [
      'cart.bundleOffer.comparePlans.plus.features.everythingInFree',
      'cart.bundleOffer.comparePlans.plus.features.posFeatures',
      'cart.bundleOffer.comparePlans.plus.features.lowerProcessingFees',
      'cart.bundleOffer.comparePlans.plus.features.loyaltyRewards',
      'cart.bundleOffer.comparePlans.plus.features.messageMarketing',
      'cart.bundleOffer.comparePlans.plus.features.staffManagement',
    ],
  },
  premium: {
    taglineKey: 'cart.bundleOffer.comparePlans.premium.tagline',
    featureKeys: [
      'cart.bundleOffer.comparePlans.premium.features.everythingInPlus',
      'cart.bundleOffer.comparePlans.premium.features.prioritySupport',
      'cart.bundleOffer.comparePlans.premium.features.advancedReporting',
      'cart.bundleOffer.comparePlans.premium.features.lowestProcessingFees',
      'cart.bundleOffer.comparePlans.premium.features.moreTextMarketing',
      'cart.bundleOffer.comparePlans.premium.features.noGiftCardLoadFees',
    ],
  },
});

const ComparePlansModal = ({
  open,
  onClose,
  tiers = {},
  recommendedTier,
  selectedTier,
  disabled = false,
  onSelectTier = () => {},
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = React.useId();
  const subtitleId = React.useId();

  const planCards = PLAN_ORDER.map((tierKey) => {
    const tier = tiers[tierKey];
    if (!tier) return null;

    const planConfig = PLAN_CONFIG[tierKey];
    const isRecommended = recommendedTier === tierKey;
    const isSelected = selectedTier === tierKey;
    const priceLabel = formatTierMonthlyPrice(tier);
    const processingRates = (tier.processing_rates ?? [])
      .map((rate) => {
        const labelKey = PROCESSING_RATE_LABEL_KEYS[rate.rate_type];
        const rateParts = formatProcessingRateParts(rate);
        if (!labelKey || rateParts === null) return null;

        return {
          labelKey,
          rateType: rate.rate_type,
          display: t(
            'cart.bundleOffer.comparePlans.processingRates.rateDisplay',
            rateParts,
          ),
        };
      })
      .filter(Boolean);

    return (
      <Box
        key={tierKey}
        sx={{
          border: 1,
          borderColor: isRecommended ? RECOMMENDED_PLAN_COLOR : 'grey.300',
          borderRadius: COMPARE_PLANS_BORDER_RADIUS,
          borderWidth: isRecommended ? 2 : 1,
          boxSizing: 'border-box',
          display: 'grid',
          gridRow: { md: 'span 7' },
          gridTemplateRows: {
            xs: 'auto auto auto auto auto minmax(0, 1fr) auto',
            md: 'subgrid',
          },
          '@supports not (grid-template-rows: subgrid)': {
            gridTemplateRows: {
              md: 'auto auto auto auto auto minmax(0, 1fr) auto',
            },
          },
          height: '100%',
          minHeight: { md: 460 },
          minWidth: 0,
          p: { xs: 3, md: 2, lg: 3 },
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          flexWrap="wrap"
          justifyContent="space-between"
          gap={2}
          minHeight={32}
        >
          <h3 className="heading-30" style={{ margin: 0, minWidth: 0 }}>
            {tier.label}
          </h3>
          {isRecommended && (
            <MarketPill
              variant="normal"
              size="small"
              style={{ cursor: 'default', flexShrink: 0 }}
            >
              {t('cart.bundleOffer.recommended')}
            </MarketPill>
          )}
        </Box>

        <Box
          component="p"
          className="paragraph-20"
          sx={{
            color: '#4A4A4A',
            m: '12px 0 20px',
            '@supports not (grid-template-rows: subgrid)': {
              minHeight: { md: 44 },
            },
          }}
        >
          {t(planConfig.taglineKey)}
        </Box>

        <Divider />

        <Box
          sx={{
            my: 3,
            '@supports not (grid-template-rows: subgrid)': {
              minHeight: { md: 64 },
            },
          }}
        >
          {priceLabel !== null && (
            <>
              <Box
                component="span"
                className="paragraph-20"
                sx={{ color: '#4A4A4A' }}
              >
                <Trans
                  i18nKey="cart.bundleOffer.comparePlans.monthlyPrice"
                  values={{ price: priceLabel }}
                  components={{
                    price: <span className="heading-30" />,
                  }}
                />
              </Box>
              <p
                className="paragraph-20"
                style={{ color: '#8C8C8C', margin: '4px 0 0' }}
              >
                {t('cart.bundleOffer.comparePlans.perLocation')}
              </p>
            </>
          )}
        </Box>

        <Box
          sx={{
            mb: 3,
            '@supports not (grid-template-rows: subgrid)': {
              minHeight: { md: 92 },
            },
          }}
        >
          {processingRates.length > 0 && (
            <>
              <p className="medium-20" style={{ margin: '0 0 8px' }}>
                {t('cart.bundleOffer.comparePlans.processingRates.heading')}
              </p>
              <Box component="dl" sx={{ display: 'grid', gap: 1, m: 0 }}>
                {processingRates.map((rate) => (
                  <Box
                    key={rate.rateType}
                    display="flex"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Box
                      component="dt"
                      className="paragraph-20"
                      sx={{ color: '#4A4A4A', m: 0 }}
                    >
                      {t(rate.labelKey)}
                    </Box>
                    <Box
                      component="dd"
                      className="paragraph-20"
                      sx={{ color: '#4A4A4A', m: 0, textAlign: 'right' }}
                    >
                      {rate.display}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            mb: 3,
            minHeight: 0,
          }}
        >
          <p className="medium-20" style={{ margin: '0 0 8px' }}>
            {t('cart.bundleOffer.comparePlans.featuresHeading')}
          </p>
          <Box
            component="ul"
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              gap: 1,
              listStyle: 'none',
              m: 0,
              p: 0,
            }}
          >
            {planConfig.featureKeys.map((featureKey) => (
              <Box
                component="li"
                display="flex"
                alignItems="flex-start"
                gap={1.25}
                key={featureKey}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    alignItems: 'center',
                    backgroundColor: '#00875A',
                    borderRadius: '50%',
                    color: 'white',
                    display: 'inline-flex',
                    flexShrink: 0,
                    fontSize: 10,
                    height: 14,
                    justifyContent: 'center',
                    lineHeight: '14px',
                    mt: '2px',
                    width: 14,
                  }}
                >
                  <svg
                    aria-hidden="true"
                    fill="none"
                    height="10"
                    viewBox="0 0 14 14"
                    width="10"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 7l3 3 5-6"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </Box>
                <Box
                  component="span"
                  className="paragraph-20"
                  sx={{ minWidth: 0, overflowWrap: 'break-word' }}
                >
                  {t(featureKey)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <MarketButton
          rank={isRecommended ? 'primary' : 'secondary'}
          {...((disabled || isSelected) && { disabled: true })}
          onClick={() => {
            if (!disabled && !isSelected) {
              onSelectTier(tierKey);
            }
          }}
          style={{
            boxSizing: 'border-box',
            maxWidth: '100%',
            minWidth: 0,
            whiteSpace: 'normal',
            width: '100%',
            ...(isRecommended ? RECOMMENDED_PLAN_BUTTON_STYLES : {}),
          }}
        >
          {isSelected
            ? t('cart.bundleOffer.comparePlans.selectedCta')
            : t('cart.bundleOffer.comparePlans.chooseCta', {
                label: tier.label,
              })}
        </MarketButton>
      </Box>
    );
  });

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={subtitleId}
      data-testid={COMPARE_PLANS_MODAL_TEST_ID}
      data-market-features="typography,html-styles"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: COMPARE_PLANS_BORDER_RADIUS },
        },
      }}
    >
      <DialogContent sx={{ px: { xs: 2, sm: 4, lg: 6 }, py: { xs: 4, sm: 5 } }}>
        <button
          aria-label={t('cart.bundleOffer.comparePlans.closeLabel')}
          onClick={onClose}
          style={{
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            display: 'inline-flex',
            height: 36,
            justifyContent: 'center',
            padding: 0,
            position: 'absolute',
            right: 20,
            top: 20,
            width: 36,
          }}
          type="button"
        >
          <CloseIcon height={16} width={16} />
        </button>

        <Box textAlign="center" mb={4} px={4}>
          <h2 id={titleId} className="heading-40" style={{ margin: 0 }}>
            {t('cart.bundleOffer.comparePlans.title')}
          </h2>
          <p
            id={subtitleId}
            className="paragraph-30"
            style={{ color: '#4A4A4A', margin: '8px 0 0' }}
          >
            {t('cart.bundleOffer.comparePlans.subtitle')}
          </p>
        </Box>

        <Box
          sx={{
            alignItems: 'stretch',
            columnGap: { md: 1.5, lg: 2 },
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gridTemplateRows: {
              md: 'auto auto auto auto auto minmax(0, 1fr) auto',
            },
            rowGap: { xs: 2, md: 0 },
          }}
        >
          {planCards}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ComparePlansModal;
